import {
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from "@heroicons/react/20/solid";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/solid";
import { useExportWallet, usePrivy, useWallets } from "@privy-io/react-auth";
import { DateTime } from "luxon";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { Alert, AlertType } from "@/atoms-react/alert/index.react";
import { Button } from "@/atoms-react/button/Button.react";
import { KypayIcon } from "@/atoms-react/crypto-icon/KypayIcon.react";
import Image from "@/atoms-react/image/Image.react";
import Loader from "@/atoms-react/loader/Loader.react";
import { Base, InfoAlert } from "@/atoms-react/text/Text.react";
import config from "@/config";
import { WebAuthnRegisterApi } from "@/features/auth/api-client/webauthn-register-api-client";
import { useKYCContext } from "@/features/auth/hooks/use-kyc-context";
import { useCguListDataQuery } from "@/features/cgu/hooks/use-get-cgu";
import { CGU_TYPE } from "@/features/cgu/types";
import { useCompany } from "@/features/company/hooks/use-company";
import { useCompanyContext } from "@/features/company/hooks/use-company-context";
import { useOrigin } from "@/features/flow/hooks/use-origin";
import usePartnerChange from "@/features/flow/hooks/use-partner-change";
import { FromURL } from "@/features/flow/types";
import { KYCSessionScenario, KYCStatusType } from "@/features/kyc/types";
import { useKycoin } from "@/features/kycoin/hooks/use-kycoin";
import type { KycoinAccount } from "@/features/kycoin/types";
import { isEtherActive, linkTokenToWallet } from "@/features/kypay/functions";
import { useKypay } from "@/features/kypay/hooks/use-kypay";
import type {
  CreateWalletReq,
  KypayAccount,
  SendKyPayTransaction,
  SendToKypayReponse,
  SendToKypayWallettReponse,
  TransferKypayAmountProps
} from "@/features/kypay/types";
import { usePartnerInfo } from "@/features/partner/hooks/use-partner-information";
import { buttonAction, partnerElementCardType, typeElement } from "@/features/partner/types";
import {
  convertCardToDesign,
  convertToDesign,
  convertToDesignCards
} from "@/features/partner/utils";
import { Routes } from "@/features/router";
import { SecurityApiClient } from "@/features/security/api-client/api-client";
import { useUser } from "@/features/user/hooks/use-user";
import { useUserCheckAccess } from "@/features/user/hooks/use-user-check-access";
import {
  ErrorWalletCodeEnum,
  type identityPartners,
  KYPAY_ACCOUNT_TYPE
} from "@/features/user/types";
import { formatNumber } from "@/features/utils/functions";
import { usei18n } from "@/features/utils/hooks/use-i18n";
import { isSystemThemeDark } from "@/features/utils/theme";
import { getWebBankingUrl } from "@/features/utils/web-banking-url";
import { ModalIbanAccount } from "@/molecules/modal-iban-account/index.react";
import { ModalKYCIdentity } from "@/molecules/modal-kyc-identity/index.react";
import { ModalIbanUniqAccount } from "@/molecules/modal-uniq-scenario/index.react";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setIsPrepareSessionError } from "@/redux/slices/kycSession";
import {
  setAddWallet,
  setAuthenticationRequireHelper,
  setBankToCrypto,
  setConfirmationCreateWallet,
  setCreateAccount,
  setCryptoToBank,
  setFeedBank,
  setReadMore,
  setTransfer,
  setWithdrawBank
} from "@/redux/slices/transfertActionSlice";
import { Themes } from "@/types/theme";
import { convertToDate } from "@/utils/date.utils";
import { getRoute } from "../../utils/routes";
import { ForcedKYCIdentityModal } from "../_layout/forced-kyc-identity-modal.react";
import { LayoutTitle } from "../_layout/index.react";
import { CompanyOnboarding_DashboardCard } from "../company-onboarding/components/company-onboarding-dashboard-card";
import { ModalAddWallet } from "../kypay/components/modal-add-wallet/index.react";
import { ModalAuthenticationRequireHelper } from "../kypay/components/modal-authentication-require/index.react";
import { ModalConfirmCreationAddWallet } from "../kypay/components/modal-create-wallet/index.react";
import { ModalFeedBank } from "../kypay/components/modal-feed-kypay-bank/index.react";
import { ModalCryptoToBank } from "../kypay/components/modal-feed-kypay-crypto/index.react";
import { ModalBankToCrypto } from "../kypay/components/modal-transfer-funds/index.react";
import { ModalTransferKypay } from "../kypay/components/modal-transfer-kypay/index.react";
import { ModalWithdrawBank } from "../kypay/components/modal-withdraw-kypay-bank/index.react";
import { AlertLoginPrivy } from "./components/alert_login_privy";
import { DiscoverOlkyCard } from "./components/card-discover-olky/index.react";
import { DashboardCard } from "./components/dashboardCard/index.react";
import { MyPartnerCard } from "./components/my-partner-card";
import type { PartnerCardListType } from "./components/my-partner-card/my-partner-card-list";
import { OlkypassCard } from "./components/olkypass-cards/index.react";
import { OlkywalletCard } from "./components/olkywallet-cards/index.react";
import { TransfertCard } from "./components/transfer_card";
import { VideoCard } from "./components/video-cards/index.react";

export const DashboardApp = (props: { origin: FromURL }) => {
  const translation = useTranslation(["pass", "wallet", "error"]);
  const { t } = translation;
  const origin = useOrigin();
  const { companyDetailQuery } = useCompany();
  const { leUuid } = useCompanyContext();
  const { checkPermission } = useUserCheckAccess();
  const { data: companyDetails, isPending: companyDetailsQueryLoading } = companyDetailQuery(
    leUuid as string
  );
  const { status, nextStatus, auditPending, kycLevel } = useKYCContext();
  const { mutateAsync: updatePartnerChange } = usePartnerChange();
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const isPrepareSessionError = useAppSelector((state) => state.kycSession.isPrepareSessionError);
  const navigate = useNavigate();
  const { kypayAccountsQuery, useUserWalletDataQuery, transferKypayAmountMutation } = useKypay();
  const { partnerDashboardDataQuery, partnerUuidQuery } = usePartnerInfo();
  const { data } = partnerDashboardDataQuery(
    "dashboard",
    (sessionStorage.getItem("origin") as string) ?? null
  );
  const { data: dataPartner } = partnerUuidQuery(
    (sessionStorage.getItem("origin") as string) ?? null
  );
  const { data: cguList } = useCguListDataQuery();
  const { useUserProfile } = useUser();
  const { data: user, refetch: refetchProfile } = useUserProfile();
  const {
    data: accounts,
    refetch: refetchAccount,
    isLoading: isLoadingAccount
  } = kypayAccountsQuery();
  const [hasOneTOTP, setHasOneTOTP] = useState<boolean>(false);
  const [isDark, setIsDark] = useState<boolean>(false);
  const [modalIdentity, setModalIdentity] = useState<boolean>(false);
  const partner = sessionStorage.getItem("origin");
  const { authenticated, user: userPrivy, logout, ready } = usePrivy();
  const { exportWallet } = useExportWallet();
  const actions = useAppSelector((state) => state.transfertActions);
  const [sendKypayToUserWalletPending, setSendKypayToUserWalletPending] = useState<boolean>(false);
  const { createUserWalletMutation } = useKypay();
  const { wallets } = useWallets();
  const [isePendingOffChain, setIsPendingOffChain] = useState<boolean>(false);
  const { sendKypayToUserWalletMutation, investPartnerMutation, sendToKypayMutation } = useKypay();
  const { mutateAsync: sendToKypay, isPending: isPendingSendToKypay } = sendToKypayMutation();

  const { mutateAsync: createUserWallet, isPending: isPendingCreationWallet } =
    createUserWalletMutation();
  const { mutateAsync: sendKypayToUserWallet } = sendKypayToUserWalletMutation();
  const { mutateAsync: investPartner } = investPartnerMutation();

  const transferOffCHain = async (amount: number) => {
    try {
      await handleSendToKypay({ amount: Number((amount * 100).toFixed(2)) });
      if (!isPendingSendToKypay) dispatch(setCryptoToBank(false));
    } catch (_) {
      toast.error(t("wallet:kypay.balance_card.off_chain_response_error"));
    }
  };
  const allowedToKypay = (() => {
    if (user?.identityPartners) {
      const found = user?.identityPartners?.find(
        (partner: identityPartners) => partner.uuid === dataPartner?.data.partner_uuid
      );
      if (found) {
        return (kycLevel as number) >= (found?.minimum_olkypass_level as number);
      }
    }
    return (kycLevel as number) >= 2;
  })();
  const idps = leUuid ? companyDetails?.identityPartners : user?.identityPartners;
  const identityPartner = (() => {
    if (idps) {
      const found = idps.find(
        (partner: identityPartners) => partner.uuid === dataPartner?.data.partner_uuid
      );
      return found ?? null;
    }
    return null;
  })();
  // Fetch "/wallet_data" //
  const {
    data: walletData,
    isPending: isWalletDataLoading,
    refetch: fetchWalletData,
    error: errorData
  } = useUserWalletDataQuery(leUuid ?? undefined, {
    enabled: allowedToKypay && !!checkPermission(null, user, companyDetails, identityPartner),
    queryKey: leUuid ? ["walletData", leUuid] : ["walletData"]
  });
  const { getLanguage } = usei18n();
  const { mutateAsync: transferKypay, isPending: pendingKypayParticipate } =
    transferKypayAmountMutation();

  const MAX_WALLET_FETCHING = 90;
  const INTERVAL_WALLET_FETCHING = 20000;

  // You may want to use this, fetching UI state
  const [isAutoFetchingWallet, setIsAutoFetchingWallet] = useState<boolean>(false);

  const autoFetchingWalletCount = useRef<number>(0);
  const autoFetchingWalletLock = useRef<boolean>(false);
  const timeoutHandler = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    if (!walletData) return;
    else if (walletData.status === null || walletData.status === "InProgress") {
      autoFetchWallet();
    }
  }, [walletData]);

  // You may want to use this
  const isTokenyWalletFailed =
    walletData?.status === "Failed" ||
    errorData?.internalCode === ErrorWalletCodeEnum.HOLDER_ID_NOT_FOUNT;
  const canAddTokens: boolean =
    isEtherActive() &&
    authenticated &&
    !isTokenyWalletFailed &&
    (walletData?.walletConnect ?? false);

  const isPartnerPage = props.origin === FromURL.Partner;

  const { getOlkyToken } = useKycoin();
  const { data: olkyToken } = getOlkyToken({
    queryKey: ["olkyToken"],
    enabled: isPartnerPage ? canAddTokens : isEtherActive()
  });

  useEffect(() => {
    checkTOTP();
    const prefersDark = isSystemThemeDark();
    if (prefersDark) {
      setIsDark(true);
    }
  }, []);
  useEffect(() => {
    const refetchProfileFn = async () => await refetchProfile();
    if (isPrepareSessionError) {
      refetchProfileFn();
      dispatch(setIsPrepareSessionError(true));
    }
  }, [isPrepareSessionError]);

  const allowedToPaticipate = (() => {
    if (identityPartner) {
      const startDate =
        identityPartner?.participation_opening_date &&
        identityPartner?.participation_opening_date !== null
          ? convertToDate(identityPartner?.participation_opening_date)
          : null;
      const endDate =
        identityPartner?.participation_closing_date &&
        identityPartner?.participation_closing_date !== null
          ? convertToDate(identityPartner?.participation_closing_date)
          : null;
      const now = DateTime.now().startOf("day");

      if (startDate && endDate === null) {
        if (startDate <= now) {
          return true;
        }
      }
      if (startDate && endDate) {
        if (endDate >= now) {
          return true;
        }
      }
      if (startDate && endDate) {
        if (startDate <= now && endDate >= now) {
          return true;
        }
      }
    }
    return false;
  })();
  const principalBalance = (() => {
    if (accounts) {
      const balance =
        (accounts?.find((account: KypayAccount) => account.is_principal_account === true)
          ?.balance ?? 0) / 100;
      return balance;
    }
  })();
  const currentAccount = (() => {
    if (accounts) {
      if (dataPartner?.data.partner_uuid) {
        return accounts?.find(
          (account: KypayAccount) => account.IDPartenaire === dataPartner.data.partner_uuid
        );
      } else {
        return accounts?.find((account: KypayAccount) => account.is_principal_account === true);
      }
    }
  })();
  const partnerList = (() => {
    if (accounts) {
      const partnerList: PartnerCardListType[] = [];
      accounts.map((account: KypayAccount) => {
        if (account.is_principal_account) return;
        const foundIdentity = user?.identityPartners?.find(
          (partner: identityPartners) => partner.uuid === account.IDPartenaire
        );
        const partnerListItem: PartnerCardListType = {
          balance: formatNumber(((account?.balance as number) ?? 0) / 100)?.toString() ?? "0",
          onChainBalance:
            formatNumber(((account?.balance_onchain as number) ?? 0) / 100)?.toString() ?? "0",
          partner: account.partner_name,
          src: foundIdentity?.logo ?? "/images/olkypass/kypay-monograms.png",
          tokenName: foundIdentity?.token.symbol as string,
          mode: foundIdentity?.mode as string,
          host: foundIdentity?.host as string,
          name: foundIdentity?.name as string
        };
        partnerList.push(partnerListItem);
      });
      return partnerList;
    }
  })();
  const handleLogoutPrivy = async () => {
    if (ready && authenticated) await logout();
  };
  const handleConnectWallet = async (requestData: CreateWalletReq) => {
    return await createUserWallet(requestData);
  };
  const transferKypayAmount = async (data: TransferKypayAmountProps) => {
    transferKypay(data)
      .then(async () => {
        toast.success(t("wallet:kypay.modal_transfer_kypay.toast.success_transfer"));
        await refetchAccount();
      })
      .catch((_) => {
        toast.error(t("wallet:kypay.modal_transfer_kypay.toast.error_transfer"));
      });
    dispatch(setFeedBank(false));
  };
  const openKYCModal = () => {
    dispatch(setCreateAccount(false));
    setModalIdentity(true);
  };
  const allowedToCardTransaction = (() =>
    identityPartner?.kypay_account_type === KYPAY_ACCOUNT_TYPE.KYPAY_PAYMENT_SOLUTION ||
    currentAccount?.allow_deposit_cb)();

  const handleSendToKypay = async (data: { amount: number }) => {
    setIsPendingOffChain(true);

    if (walletData?.walletConnect) {
      const sendToKypayRes = (await sendToKypay(data)) as SendToKypayReponse;

      if (sendToKypayRes?.success) {
        await sendEthTransaction(sendToKypayRes.data.data);
      }
    } else if (walletData?.olkyWallet) {
      const sendToKypayRes = (await sendToKypay(data)) as SendToKypayWallettReponse;
      if (sendToKypayRes.data.transferDetails.status === "Broadcasted") {
        toast.success(t("wallet:kypay.balance_card.off_chain_response"));
      } else {
        toast.error(t("wallet:kypay.balance_card.off_chain_response_error"));
      }
    }
    setIsPendingOffChain(false);
  };
  const sendEthTransaction = async (transaction: SendKyPayTransaction) => {
    try {
      const embeddedWallet = wallets.find((w) => w.walletClientType === "privy") ?? wallets[0];
      const provider = await embeddedWallet.getEthereumProvider();

      await provider.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: config.SITE_NETCHAIN_ID }]
      });

      const gasEstimate = await provider.request({
        method: "eth_estimateGas",
        params: [transaction]
      });

      await provider.request({
        method: "eth_sendTransaction",
        params: [{ ...transaction, gas: gasEstimate }]
      });
    } catch (_error) {
      console.error(_error);
    }
  };
  const disableHeaderButton = (() => {
    const userPermission = checkPermission(null, user, companyDetails, identityPartner);
    switch (convertToDesign(data, typeElement.BUTTON)?.action) {
      case buttonAction.PARTICIPATE:
        return !userPermission || !allowedToPaticipate;
      case buttonAction.REDIRECT:
        return !userPermission;
      case buttonAction.CREATE_ACCOUNT:
        return (
          (!user?.naturalPerson.codeShop && user?.naturalPerson.olkypay_account_requested === 1) ||
          auditPending ||
          user?.naturalPerson.codeShop !== null
        );
    }
  })();

  const checkTOTP = async () => {
    const is2faActive = await SecurityApiClient.is2faActive();
    const isWebAuthnActive = (await WebAuthnRegisterApi.checkActivation())?.isWebAuthnActivated;
    if (is2faActive || isWebAuthnActive) {
      setHasOneTOTP(true);
    }
  };
  const handleButtonModalAction = (typeaction: string | undefined, redirect_url?: string) => {
    switch (typeaction?.toLocaleUpperCase()) {
      case buttonAction.PARTICIPATE:
        if (checkPermission(null, user, companyDetails, identityPartner) && allowedToPaticipate) {
          dispatch(setFeedBank(true));
        }
        break;

      case buttonAction.READMORE:
        {
          if (props.origin === FromURL.None) dispatch(setReadMore(true));
          else {
            window.open(redirect_url, "_blank");
          }
        }
        break;
      case buttonAction.REDIRECT:
        if (checkPermission(null, user, companyDetails, identityPartner)) {
          window.open(redirect_url, "_blank");
        }
        break;
      case buttonAction.CREATE_ACCOUNT:
        if (checkPermission(null, user, companyDetails, identityPartner)) {
          dispatch(setCreateAccount(true));
        }
        break;

      default:
        break;
    }
  };
  const FooterTerm = () => {
    const term = convertToDesign(data, typeElement.HTML_TERM)
      ? getLanguage() === "fr"
        ? convertToDesign(data, typeElement.HTML_TERM)?.content_fr
        : convertToDesign(data, typeElement.HTML_TERM)?.content_en
      : undefined;
    return (
      <>
        {term ? (
          <div dangerouslySetInnerHTML={{ __html: term }}></div>
        ) : isPartnerPage ? (
          identityPartner?.mode !== "PRO" && identityPartner?.mode !== "CONSO" ? (
            <Base className="mt-4 text-black text-sm dark:text-slate-50">
              <Trans
                i18nKey={
                  identityPartner?.kypay_account_type ===
                  KYPAY_ACCOUNT_TYPE.KYPAY_PAYMENT_INSTRUMENT
                    ? "olkypass.mainPage.olkypass.cgu_partner_payment_instrument"
                    : "olkypass.mainPage.olkypass.cgu_partner_payment_solution"
                }
                components={{
                  bold: <div className="inline font-bold"> </div>,
                  cgu_pass: (
                    <a
                      className="font-bold underline dark:text-white"
                      target="_blank"
                      href={
                        getLanguage() === "fr"
                          ? cguList?.find((cgu) => cgu.Type === CGU_TYPE.REGISTER)?.UrlFr
                          : cguList?.find((cgu) => cgu.Type === CGU_TYPE.REGISTER)?.UrlEn
                      }
                    ></a>
                  ),
                  cgu: (
                    <a
                      className="font-bold underline dark:text-white"
                      target="_blank"
                      href={
                        getLanguage() === "fr"
                          ? cguList?.find((cgu) => cgu.Type === CGU_TYPE.TRANSACTION)?.UrlFr
                          : cguList?.find((cgu) => cgu.Type === CGU_TYPE.TRANSACTION)?.UrlEn
                      }
                    ></a>
                  )
                }}
                values={{
                  partner: `${partner?.charAt(0)?.toUpperCase()}${partner?.slice(1)}`
                }}
              ></Trans>
            </Base>
          ) : (
            <Base className="mt-4 text-black text-sm dark:text-slate-50">
              <Trans
                i18nKey={"olkypass.mainPage.olkypass.cgu_partner_conso"}
                components={{
                  bold: <div className="inline font-bold"> </div>,
                  cgu_pass: (
                    <a
                      className="font-bold underline dark:text-white"
                      target="_blank"
                      href={
                        getLanguage() === "fr"
                          ? cguList?.find((cgu) => cgu.Type === CGU_TYPE.REGISTER)?.UrlFr
                          : cguList?.find((cgu) => cgu.Type === CGU_TYPE.REGISTER)?.UrlEn
                      }
                    ></a>
                  ),
                  cgu: (
                    <a
                      className="font-bold underline dark:text-white"
                      target="_blank"
                      href={
                        getLanguage() === "fr"
                          ? cguList?.find((cgu) => cgu.Type === CGU_TYPE.TRANSACTION)?.UrlFr
                          : cguList?.find((cgu) => cgu.Type === CGU_TYPE.TRANSACTION)?.UrlEn
                      }
                    ></a>
                  )
                }}
                values={{
                  partner: `${partner?.charAt(0)?.toUpperCase()}${partner?.slice(1)}`
                }}
              ></Trans>
            </Base>
          )
        ) : (
          <Base className="mt-4 text-black text-sm dark:text-slate-50">
            <Trans
              i18nKey={"olkypass.mainPage.olkypass.cgu"}
              components={{
                bold: <div className="inline font-bold"> </div>,
                cgu: (
                  <a
                    className="font-bold underline dark:text-white"
                    target="_blank"
                    href={
                      getLanguage() === "fr"
                        ? cguList?.find((cgu) => cgu.Type === CGU_TYPE.TRANSACTION)?.UrlFr
                        : cguList?.find((cgu) => cgu.Type === CGU_TYPE.TRANSACTION)?.UrlEn
                    }
                  ></a>
                )
              }}
              values={{
                partner: `${partner?.charAt(0)?.toUpperCase()}${partner?.slice(1)}`
              }}
            ></Trans>
          </Base>
        )}
      </>
    );
  };
  const getHeader = () => {
    switch (props.origin) {
      case FromURL.Partner:
        return (
          <LayoutTitle bgImage={convertToDesign(data, typeElement.BANNER)?.url_fr}>
            {convertToDesign(data, typeElement.BANNER) ? (
              <>
                <Image
                  className="mt-4 w-full 2xl:mt-0"
                  alt=""
                  src={
                    getLanguage() === "fr"
                      ? convertToDesign(data, typeElement.BANNER)?.url_fr
                      : convertToDesign(data, typeElement.BANNER)?.url_en
                  }
                />
              </>
            ) : (
              <Base className="w-1/3 font-bold text-2xl text-black dark:text-white">
                {identityPartner?.name ?? t("business.label.main_title")}
              </Base>
            )}
          </LayoutTitle>
        );
      default:
        return (
          <>
            <LayoutTitle
              dataCy="dashboard-title"
              actionSupport={
                <>
                  <Button
                    size="sm"
                    theme={Themes.white}
                    onClick={() => navigate(getRoute(Routes.support))}
                    icon={<QuestionMarkCircleIcon />}
                    iconPosition="before"
                    className="w-max"
                  >
                    {t("business.button.contact_support")}
                  </Button>
                </>
              }
              action={
                <>
                  {isEtherActive() && olkyToken && (
                    <Button
                      size="md"
                      theme={Themes.white}
                      onClick={async () => {
                        if (olkyToken?.symbol && olkyToken?.decimal) {
                          await linkTokenToWallet(olkyToken);
                        }
                      }}
                      icon={<KypayIcon />}
                      iconPosition="before"
                      className="w-max"
                    >
                      {t("wallet:kypay.button.add_kypay_token", { token: "KYPAY" })}
                    </Button>
                  )}
                  {ready &&
                    authenticated &&
                    walletData?.walletConnect &&
                    !isTokenyWalletFailed &&
                    userPrivy?.wallet &&
                    userPrivy?.wallet.walletClientType === "privy" &&
                    userPrivy?.wallet?.address && (
                      <Button
                        size="md"
                        theme={Themes.white}
                        className="ml-1 w-max"
                        onClick={() => {
                          if (!userPrivy.wallet?.address) return;
                          exportWallet({ address: userPrivy.wallet?.address });
                        }}
                      >
                        {t("wallet:kypay.button.export_wallet")}
                      </Button>
                    )}
                </>
              }
            >
              <div className="flex flex-row">
                {leUuid ? (
                  companyDetailsQueryLoading ? (
                    <Loader />
                  ) : (
                    <div className="flex items-center gap-x-2">
                      <span>{`${t("business.label.main_title")}${leUuid ? " - " : ""}`}</span>
                      {leUuid && (
                        <span className="text-pblue-500">{`${
                          companyDetails?.legalEntity?.LegalName ?? ""
                        }`}</span>
                      )}
                    </div>
                  )
                ) : (
                  <div className="flex items-center gap-x-2">
                    {`${t("business.label.main_title")}`}
                  </div>
                )}
              </div>
            </LayoutTitle>
          </>
        );
    }
  };
  const Body = () => (
    <TransfertCard
      isAutoFetchingWallet={isAutoFetchingWallet}
      isLoadingBalanceKypayOnChain={isLoadingAccount}
      currentAccount={currentAccount as KycoinAccount}
      companyDetails={companyDetails}
      isWalletLoading={isWalletDataLoading}
      hasOneTOTP={hasOneTOTP}
      wallet={walletData}
      isTokenyWalletFailed={isTokenyWalletFailed}
      allowedToKypay={checkPermission(null, user, companyDetails, identityPartner)}
      isLoadingBalanceKypay={isLoadingAccount}
      balanceKypayOnChain={
        currentAccount && !isNaN(currentAccount?.balance_onchain as number)
          ? formatNumber(((currentAccount?.balance_onchain as number) ?? 0) / 100)
          : "0"
      }
      isWalletDataLoading={isWalletDataLoading}
      balanceKypayOnChainCurrency={currentAccount?.symbol}
      balanceKypay={formatNumber((currentAccount?.balance ?? 0) / 100) as unknown as number}
      userProfile={user}
      identityPartner={identityPartner}
      allowedToPaticipate={allowedToPaticipate && !!currentAccount}
      refetchAccount={refetchAccount}
      btnText={
        getLanguage() === "fr"
          ? convertToDesign(data, typeElement.BUTTON)?.content?.content_fr
          : convertToDesign(data, typeElement.BUTTON)?.content?.content_en
      }
    />
  );
  const redirectToBankAccount = () => {
    let email = user?.naturalPerson?.AliasEmail.find((email) => email.Actif === true)?.Mail;
    if (!email && (user?.naturalPerson?.AliasEmail?.length as number) > 0) {
      email = user?.naturalPerson?.AliasEmail[0].Mail;
    }
    const url = getWebBankingUrl(email);
    const success = window.open(url, "_blank");
    if (!success) document.location = url;
  };
  const lockAutoFetchingWallet = (lock: boolean) => {
    setIsAutoFetchingWallet(lock);
    autoFetchingWalletLock.current = lock;
    if (!lock) clearTimeout(timeoutHandler.current);
  };

  const recursiveFetch = () => {
    clearTimeout(timeoutHandler?.current);

    timeoutHandler.current = setTimeout(async () => {
      const { data: newWallet, error: newWalletError } = await fetchWalletData();

      if (newWalletError) {
        lockAutoFetchingWallet(false);
        return;
      }

      if (!newWallet || newWallet.status === null || newWallet.status === "InProgress") {
        autoFetchingWalletCount.current++;
        if (autoFetchingWalletCount?.current < MAX_WALLET_FETCHING) {
          recursiveFetch();
        }
      } else {
        setIsAutoFetchingWallet(false);
      }
    }, INTERVAL_WALLET_FETCHING);
  };

  // You may want to use this, call this function to start autofetching on wallet_data
  const autoFetchWallet = () => {
    if (autoFetchingWalletLock.current) return;
    lockAutoFetchingWallet(true);
    recursiveFetch();
  };
  const [showChevron, setShowChevron] = useState(false);
  const getHeaderInfo = () => {
    switch (props.origin) {
      case FromURL.Partner:
        return (
          <Alert
            type={AlertType.Info}
            title={
              getLanguage() === "fr"
                ? convertToDesign(data, typeElement.TITLE)?.content_fr
                : convertToDesign(data, typeElement.TITLE)?.content_en
            }
          >
            <div className="hidden md:block">
              <InfoAlert className="mt-2 block">
                {getLanguage() === "fr"
                  ? convertToDesign(data, typeElement.DESCRIPTION)?.content_fr
                  : convertToDesign(data, typeElement.DESCRIPTION)?.content_en}
              </InfoAlert>
              {!!convertToDesign(data, typeElement.BUTTON) && (
                <div className="flex flex-row">
                  {convertToDesign(data, typeElement.BUTTON)?.action ===
                    buttonAction.CREATE_ACCOUNT && user?.naturalPerson.codeShop ? (
                    <Button
                      size="md"
                      theme={Themes.white}
                      onClick={redirectToBankAccount}
                      className="mt-4 flex w-26 flex-row justify-start bg-white bg-white text-black text-primary text-sm dark:bg-white dark:text-black dark:text-white hover:dark:bg-slate-200"
                      icon={<ArrowTopRightOnSquareIcon />}
                    >
                      {t("olkypass.mainPage.olkypass.bloc2.btn_access_partner", {
                        partner: `${partner?.charAt(0)?.toUpperCase()}${partner?.slice(1)}`
                      })}
                    </Button>
                  ) : (
                    <Button
                      size="md"
                      theme={Themes.white}
                      disabled={
                        (convertToDesign(data, typeElement.BUTTON)?.action ===
                          buttonAction.PARTICIPATE &&
                          (!checkPermission(null, user, companyDetails, identityPartner) ||
                            !allowedToPaticipate ||
                            !currentAccount)) ||
                        (convertToDesign(data, typeElement.BUTTON)?.action ===
                          buttonAction.REDIRECT &&
                          !checkPermission(null, user, companyDetails, identityPartner)) ||
                        (convertToDesign(data, typeElement.BUTTON)?.action ===
                          buttonAction.CREATE_ACCOUNT &&
                          (!checkPermission(null, user, companyDetails, identityPartner) ||
                            (!user?.naturalPerson.codeShop &&
                              user?.naturalPerson.olkypay_account_requested === 1) ||
                            auditPending ||
                            user?.naturalPerson.codeShop !== null))
                      }
                      onClick={() =>
                        handleButtonModalAction(
                          convertToDesign(data, typeElement.BUTTON)?.action,
                          convertToDesign(data, typeElement.BUTTON)?.redirect_url
                        )
                      }
                      className="mt-4 flex w-26 flex-row justify-start bg-white bg-white text-black text-primary text-sm dark:bg-white dark:text-black dark:text-white hover:dark:bg-slate-200"
                    >
                      {getLanguage() === "fr"
                        ? convertToDesign(data, typeElement.BUTTON)?.content?.content_fr
                        : convertToDesign(data, typeElement.BUTTON)?.content?.content_en}
                    </Button>
                  )}
                  {!walletData &&
                    !isTokenyWalletFailed &&
                    identityPartner?.mode === "TOKEN" &&
                    identityPartner?.token?.address &&
                    !isAutoFetchingWallet &&
                    ready && (
                      <Button
                        size="md"
                        theme={Themes.white}
                        disabled={
                          !allowedToKypay ||
                          !!walletData ||
                          errorData?.internalCode === ErrorWalletCodeEnum.HOLDER_ID_NOT_FOUNT
                        }
                        onClick={async () => {
                          if (ready && authenticated) await logout();
                          dispatch(setAddWallet(true));
                        }}
                        className="mt-4 ml-2 flex w-26 flex-row justify-start bg-white bg-white text-black text-primary text-sm dark:bg-white dark:text-black dark:text-white hover:dark:bg-slate-200"
                      >
                        {t("wallet:kypay.modal_add_wallet.label.warning_action_required_button")}
                      </Button>
                    )}
                </div>
              )}
            </div>
            <div className="px-8 sm:px-0 md:hidden">
              {showChevron ||
              (getLanguage() === "fr" &&
                convertToDesign(data, typeElement.BUTTON)?.content?.content_fr) ||
              (getLanguage() === "en" &&
                convertToDesign(data, typeElement.BUTTON)?.content?.content_en) ? (
                <>
                  <InfoAlert className="mt-2 block">
                    {getLanguage() === "fr"
                      ? convertToDesign(data, typeElement.DESCRIPTION)?.content_fr
                      : convertToDesign(data, typeElement.DESCRIPTION)?.content_en}
                  </InfoAlert>
                  <Button
                    size="md"
                    theme={Themes.white}
                    disabled={disableHeaderButton}
                    onClick={() =>
                      handleButtonModalAction(
                        convertToDesign(data, typeElement.BUTTON)?.action ?? undefined,
                        convertToDesign(data, typeElement.BUTTON)?.redirect_url ?? undefined
                      )
                    }
                    className="mt-4 flex w-26 flex-row justify-start bg-white bg-white text-black text-primary text-sm dark:bg-white dark:text-black dark:text-white hover:dark:bg-slate-200"
                  >
                    {getLanguage() === "fr"
                      ? convertToDesign(data, typeElement.BUTTON)?.content?.content_fr
                      : convertToDesign(data, typeElement.BUTTON)?.content?.content_en}
                  </Button>
                  {!(
                    (getLanguage() === "fr" &&
                      convertToDesign(data, typeElement.BUTTON)?.content?.content_fr) ||
                    (getLanguage() === "en" &&
                      convertToDesign(data, typeElement.BUTTON)?.content?.content_en)
                  ) && (
                    <div className="flex w-full flex-row justify-end">
                      <ChevronUpIcon onClick={() => setShowChevron(false)} className="ml-2 w-8" />
                    </div>
                  )}
                </>
              ) : (
                <div>
                  <InfoAlert className="mt-2 block truncate md:whitespace-normal">
                    {getLanguage() === "fr"
                      ? convertToDesign(data, typeElement.DESCRIPTION)?.content_fr?.slice(0, 70)
                      : convertToDesign(data, typeElement.DESCRIPTION)?.content_en?.slice(0, 70)}
                    ...
                  </InfoAlert>
                  <div className="flex w-full flex-row justify-end">
                    <ChevronDownIcon onClick={() => setShowChevron(true)} className="ml-2 w-8" />
                  </div>
                </div>
              )}
            </div>
          </Alert>
        );

      default:
        return <></>;
    }
  };

  const updatePartner = async (
    partner: FromURL,
    url: string,
    partnerType?: string,
    lang?: string
  ) => {
    setActionLoading(true);
    try {
      if (authenticated) await logout();
      await updatePartnerChange({
        partner: partner,
        url,
        partnerType: partnerType as string,
        lang: lang as string
      });
    } catch (_) {
      setActionLoading(false);
    }
  };

  const CardContentByOrigin = () => {
    if (status === null || nextStatus === null) return <Loader />;
    switch (props.origin) {
      case FromURL.Partner:
        return (
          <div className="mb-9 flex w-full flex-col gap-y-2 md:gap-x-3 lg:mb-10 lg:flex-row">
            <OlkypassCard
              currentStatus={status}
              dashboard={true}
              nextStatus={nextStatus}
              auditPending={auditPending}
              CTA={
                status === KYCStatusType.DECLARATIVE && user?.naturalPerson.DateOfBirth !== null
                  ? false
                  : true
              }
              verifyButton
              className="xl:h-[223px]"
            />
            {(data?.cards ?? []).map((card, index: number) => (
              <div
                key={index}
                className="w-full border-slate-50 md:hidden lg:block xl:w-4/12 dark:border-slate-800"
              >
                <DashboardCard
                  allowedToKypay={checkPermission(null, user, companyDetails, identityPartner)}
                  allowedToPaticipate={allowedToPaticipate && !!currentAccount}
                  user={user}
                  openAction={() =>
                    handleButtonModalAction(
                      convertCardToDesign(card, typeElement.CARD_BUTTON)?.action,
                      convertCardToDesign(card, typeElement.CARD_BUTTON)?.redirect_url
                    )
                  }
                  action={convertCardToDesign(card, typeElement.CARD_BUTTON)?.action}
                  btnText={
                    getLanguage() === "fr"
                      ? convertCardToDesign(card, typeElement.CARD_BUTTON)?.content?.content_fr
                      : (convertCardToDesign(card, typeElement.CARD_BUTTON)?.content?.content_en ??
                        undefined)
                  }
                  bgUrl={
                    getLanguage() === "fr"
                      ? convertCardToDesign(card, typeElement.BACKGROUND)?.url_fr
                      : (convertCardToDesign(card, typeElement.BACKGROUND)?.url_en ?? undefined)
                  }
                  button_text_attribute={
                    convertCardToDesign(card, typeElement.CARD_BUTTON)?.content?.attribues
                  }
                />
              </div>
            ))}
            <div className="hidden gap-x-4 md:block md:flex md:flex-row md:justify-between lg:hidden">
              {(data?.cards ?? []).map(
                (card: partnerElementCardType, index: number | null | undefined) => (
                  <div key={index} className="mt-2 w-1/2">
                    {" "}
                    <DashboardCard
                      allowedToKypay={checkPermission(null, user, companyDetails, identityPartner)}
                      allowedToPaticipate={allowedToPaticipate && !!currentAccount}
                      openAction={() =>
                        handleButtonModalAction(
                          convertCardToDesign(card, typeElement.CARD_BUTTON)?.action ?? undefined,
                          convertCardToDesign(card, typeElement.CARD_BUTTON)?.redirect_url ??
                            undefined
                        )
                      }
                      action={
                        convertCardToDesign(card, typeElement.CARD_BUTTON)?.action ?? undefined
                      }
                      btnText={
                        (getLanguage() === "fr"
                          ? convertCardToDesign(card, typeElement.CARD_BUTTON)?.content?.content_fr
                          : convertCardToDesign(card, typeElement.CARD_BUTTON)?.content
                              ?.content_en) ?? undefined
                      }
                      bgUrl={
                        (getLanguage() === "fr"
                          ? convertCardToDesign(card, typeElement.BACKGROUND)?.url_fr
                          : convertCardToDesign(card, typeElement.BACKGROUND)?.url_en) ?? undefined
                      }
                      button_text_attribute={
                        convertCardToDesign(card, typeElement.CARD_BUTTON)?.content?.attribues ??
                        undefined
                      }
                    />
                  </div>
                )
              )}
            </div>
          </div>
        );
      default:
        return (
          <div className="mb-9 flex w-full flex-col flex-wrap gap-y-2 md:gap-x-2 lg:mb-10 lg:flex-row">
            <OlkypassCard
              currentStatus={status}
              dashboard={true}
              nextStatus={nextStatus}
              auditPending={auditPending}
              CTA={
                status === KYCStatusType.DECLARATIVE && user?.naturalPerson.DateOfBirth !== null
                  ? false
                  : true
              }
              verifyButton
            />
            <div className="block flex-1 md:hidden lg:block xl:w-4/12">
              <DiscoverOlkyCard
                openKYCModal={openKYCModal}
                user={user}
                auditPending={auditPending}
                leUuid={leUuid ?? undefined}
              />
            </div>

            <div className="flex-1 md:hidden lg:block xl:w-4/12">
              {!user?.naturalPerson?.olkyAccount?.iban ? <VideoCard /> : <OlkywalletCard />}
            </div>

            <div className="hidden md:block md:flex md:flex-row md:justify-between lg:hidden">
              <div className="w-1/2">
                <DiscoverOlkyCard
                  user={user}
                  openKYCModal={openKYCModal}
                  auditPending={auditPending}
                  leUuid={leUuid ?? undefined}
                />
              </div>
              <div className="ml-2 w-1/2">
                {!user?.naturalPerson?.olkyAccount?.iban ? <VideoCard /> : <OlkywalletCard />}
              </div>
            </div>
          </div>
        );
        break;
    }
  };
  if (actionLoading) {
    return (
      <>
        <Loader className="absolute top-0 right-0 bottom-0 left-0 m-auto" />
      </>
    );
  }

  return (
    <>
      {!leUuid &&
        (props.origin === FromURL.None ||
          (props.origin === FromURL.Partner &&
            !!identityPartner &&
            ((identityPartner.minimum_olkypass_level > 1 &&
              identityPartner?.custom_scenario !== KYCSessionScenario.Fusion) ||
              (identityPartner?.custom_scenario === KYCSessionScenario.Fusion &&
                identityPartner.minimum_olkypass_level === 1 &&
                kycLevel &&
                kycLevel < 2)))) && (
          <ForcedKYCIdentityModal
            bannerCreateAccountUrl={
              getLanguage() === "fr"
                ? convertToDesignCards(data?.cards?.[0], typeElement.BANNER)?.url_fr
                : convertToDesignCards(data?.cards?.[0], typeElement.BANNER)?.url_en
            }
            identityPartners={identityPartner}
          />
        )}
      {getHeader()}
      <div className="mb-4">{getHeaderInfo()}</div>
      <CompanyOnboarding_DashboardCard />
      {leUuid && companyDetails?.legalEntity.IsWaitingConsent && (
        <Alert
          title={t("business_links.consentdocument.sign.alert_title")}
          className="mb-4"
          type={AlertType.Warning}
        >
          <InfoAlert className="mt-2 block h-full text-alert_info-text text-warning">
            {t("business_links.consentdocument.sign.alert")}
          </InfoAlert>
        </Alert>
      )}
      <div className="mb-5 flex flex-row justify-end">
        {/* We may want to merge this part to getHeader() ? */}
        {isPartnerPage && canAddTokens && (
          <Button
            size="sm"
            theme={Themes.white}
            onClick={async () => {
              if (olkyToken?.symbol && olkyToken?.decimal && identityPartner?.logo) {
                await linkTokenToWallet(olkyToken, identityPartner.logo);
              }
            }}
            icon={
              identityPartner?.logo ? (
                <div
                  className={twMerge(
                    "flex h-5 w-5 flex-row justify-center rounded-full bg-black dark:bg-transparent",
                    !identityPartner?.logo && "bg-transparent"
                  )}
                >
                  <div className="flex flex-col justify-center">
                    <img
                      alt={"logo"}
                      src={identityPartner?.logo ?? "/images/olkypass/kypayIcon.png"}
                      className="h-3 w-3"
                    />
                  </div>
                </div>
              ) : (
                <></>
              )
            }
            iconPosition="before"
            className="mr-4 hidden md:block md:w-max md:overflow-visible"
          >
            {t("wallet:kypay.button.add_kypay_token", {
              token: identityPartner?.name.toUpperCase()
            })}
          </Button>
        )}
        {isPartnerPage &&
          ready &&
          authenticated &&
          !isTokenyWalletFailed &&
          walletData?.walletConnect &&
          userPrivy?.wallet &&
          userPrivy?.wallet.walletClientType === "privy" && (
            <Button
              size="sm"
              theme={Themes.white}
              onClick={() => {
                if (!userPrivy.wallet?.address) return;
                exportWallet({ address: userPrivy?.wallet?.address });
              }}
              className={"w-max"}
            >
              {t("wallet:kypay.button.export_wallet")}
            </Button>
          )}
      </div>

      {status === null || nextStatus === null ? (
        <div className="flex items-center justify-center">
          <Loader />
        </div>
      ) : (
        <CardContentByOrigin />
      )}

      {isPartnerPage && (
        <>
          {!identityPartner ? (
            <></>
          ) : identityPartner?.mode !== "PRO" && identityPartner?.mode !== "CONSO" ? (
            <Body />
          ) : (
            <> </>
          )}
        </>
      )}
      {props.origin === FromURL.None && <Body />}
      {walletData && isTokenyWalletFailed && config.USE_PRIVY && (
        <>
          {((origin === FromURL.Partner &&
            identityPartner?.mode === "TOKEN" &&
            identityPartner?.token?.address) ||
            origin === FromURL.None) && (
            <Alert
              title={t("wallet:kypay.warning_wallet_status_is_failed.title")}
              className="mt-4"
              type={AlertType.Warning}
            >
              <InfoAlert>
                <p className="text-orange-500">
                  {t("wallet:kypay.warning_wallet_status_is_failed.description")}
                  <a
                    className="ml-1 cursor-pointer font-bold underline"
                    onClick={() => navigate(getRoute(Routes.support))}
                  >
                    {t("business.button.contact_support")}
                  </a>
                </p>
              </InfoAlert>
            </Alert>
          )}
        </>
      )}
      {
        //24006 error is when the wallet exist but user is not imported in tokeny
        errorData?.internalCode === ErrorWalletCodeEnum.HOLDER_ID_NOT_FOUNT && (
          <>
            {((origin === FromURL.Partner &&
              identityPartner?.mode === "TOKEN" &&
              identityPartner?.token?.address) ||
              origin === FromURL.None) && (
              <Alert
                title={t("wallet:kypay.warning_wallet_status_is_failed.title")}
                className="mt-4"
                type={AlertType.Danger}
              >
                <InfoAlert>
                  <p className="text-error">
                    {t("wallet:kypay.warning_wallet_status_is_failed.description")}
                    <a
                      className="ml-1 cursor-pointer font-bold underline"
                      onClick={() => navigate(getRoute(Routes.support))}
                    >
                      {t("business.button.contact_support")}
                    </a>
                  </p>
                </InfoAlert>
              </Alert>
            )}
          </>
        )
      }
      {ready && (
        <AlertLoginPrivy
          walletData={walletData}
          isTokenyWalletFailed={isTokenyWalletFailed}
          identityPartner={identityPartner}
        />
      )}

      {walletData &&
        walletData?.walletConnect &&
        ready &&
        authenticated &&
        config.USE_PRIVY &&
        walletData.walletAddress.toLocaleLowerCase().trim() !==
          userPrivy?.wallet?.address.toLocaleLowerCase().trim() &&
        ((origin === FromURL.Partner &&
          identityPartner?.mode === "TOKEN" &&
          identityPartner?.token?.address) ||
          origin === FromURL.None) && (
          <Alert
            title={t("wallet:kypay.label.error_connection")}
            className="mt-4"
            type={AlertType.Info}
          >
            <InfoAlert>
              <p>
                {t("wallet:kypay.label.warning1")}
                <span className="">{walletData.walletAddress}</span>.
              </p>{" "}
              <p>{t("wallet:kypay.label.warning1.5")}</p>
              <div className="mt-2 flex w-full flex-row justify-start">
                <Button
                  size="sm"
                  theme={Themes.white}
                  onClick={async () => {
                    await logout();
                  }}
                  className={"w-max"}
                >
                  {t("wallet:kypay.button.disconnect_wallet")}
                </Button>
              </div>
            </InfoAlert>
          </Alert>
        )}
      {actions.createAccount && !auditPending ? (
        identityPartner?.custom_scenario === KYCSessionScenario.Fusion &&
        status === KYCStatusType.DECLARATIVE ? (
          <ModalIbanUniqAccount
            isOpen={actions.createAccount}
            bannerCreateAccountUrl={
              getLanguage() === "fr"
                ? convertToDesignCards(data?.cards?.[0], typeElement.BANNER)?.url_fr
                : convertToDesignCards(data?.cards?.[0], typeElement.BANNER)?.url_en
            }
            onClose={() => dispatch(setCreateAccount(false))}
          />
        ) : (
          <ModalIbanAccount
            allowedConsoPartner={checkPermission(null, user, companyDetails, identityPartner)}
            isOpen={actions.createAccount}
            onClose={() => {
              sessionStorage.removeItem("DocumentKsyc");
              sessionStorage.removeItem("DocumentQuizKyc");
              dispatch(setCreateAccount(false));
            }}
            openKYCModal={openKYCModal}
          />
        )
      ) : (
        <></>
      )}
      {modalIdentity && (
        <ModalKYCIdentity onClose={() => setModalIdentity(false)} open={modalIdentity} />
      )}
      {props.origin === FromURL.None && partnerList && partnerList?.length > 0 && (
        <MyPartnerCard
          updatePartner={updatePartner}
          isDark={isDark}
          partnerList={partnerList as PartnerCardListType[]}
        />
      )}
      {actions.transfer && (
        <ModalTransferKypay
          open={actions.transfer}
          accounts={accounts}
          onClose={() => {
            dispatch(setTransfer(false));
          }}
        />
      )}
      {actions.withdrawBank && (
        <ModalWithdrawBank
          open={actions.withdrawBank}
          kypay={principalBalance}
          onClose={() => dispatch(setWithdrawBank(false))}
        />
      )}

      {actions.feedBank && (
        <ModalFeedBank
          currentAccount={currentAccount}
          cguList={cguList ?? []}
          allowedToKypay={checkPermission(null, user, companyDetails, identityPartner)}
          btnText={
            getLanguage() === "fr"
              ? convertToDesign(data, typeElement.BUTTON)?.content?.content_fr
              : convertToDesign(data, typeElement.BUTTON)?.content?.content_en
          }
          allowedToCardTransaction={allowedToCardTransaction}
          balanceKypay={formatNumber(principalBalance as number) as unknown as number}
          kypay={principalBalance}
          transferKypayAmount={transferKypayAmount}
          pendingKypayParticipate={pendingKypayParticipate}
          identityPartner={identityPartner}
          open={actions.feedBank}
          onClose={() => {
            dispatch(setFeedBank(false));
            dispatch(setFeedBank(false));
          }}
        />
      )}
      {actions.cryptoToBank && (
        <>
          <ModalCryptoToBank
            open={actions.cryptoToBank}
            onClose={() => dispatch(setCryptoToBank(false))}
            wallet={walletData}
            onSetWallet={async () => {
              if (ready && authenticated) await logout();
              dispatch(setAddWallet(true));
            }}
            isPendingSendToKypay={isPendingSendToKypay || isePendingOffChain}
            onValidated={async (amount) => {
              await transferOffCHain(amount);
              await refetchAccount();
              dispatch(setCryptoToBank(false));
            }}
          />
        </>
      )}
      {actions.addWallet && !walletData?.walletAddress && !walletData?.walletConnect && (
        <ModalAddWallet
          walletData={walletData}
          open={actions.addWallet}
          onClose={() => dispatch(setAddWallet(false))}
          wallet={walletData}
          isWalletLoading={isWalletDataLoading}
          onValidated={autoFetchWallet}
          userEmail={user?.naturalPerson.AliasEmail[0].Mail}
          address={userPrivy?.wallet?.address}
        />
      )}

      {actions.confirmationCreationWallet && (
        <ModalConfirmCreationAddWallet
          handleConnectWallet={handleConnectWallet}
          autoFetchWallet={autoFetchWallet}
          onClose={() => dispatch(setConfirmationCreateWallet(false))}
          wallet={userPrivy?.wallet}
          authenticated={authenticated}
          confirmationPopup={actions.confirmationCreationWallet}
          handleLogoutPrivy={handleLogoutPrivy}
          ready={ready}
          isPendingCreationWallet={isPendingCreationWallet}
        />
      )}

      {actions.bankToCrypto && (
        <ModalBankToCrypto
          wallet={walletData?.walletAddress ?? null}
          open={actions.bankToCrypto}
          balanceKypay={((currentAccount?.balance ?? 0) / 100) as unknown as number}
          sendKypayToUserWalletPending={sendKypayToUserWalletPending}
          onClose={() => dispatch(setBankToCrypto(false))}
          onValidated={async (amount) => {
            setSendKypayToUserWalletPending(true);
            try {
              if (isPartnerPage) await investPartner({ amount: Number((amount * 100).toFixed(2)) });
              else await sendKypayToUserWallet({ amount: Number((amount * 100).toFixed(2)) });
              setTimeout(() => {
                dispatch(setBankToCrypto(false));
                setSendKypayToUserWalletPending(false);
                refetchAccount();
                toast.success(t("wallet:kypay.label.warning_transafer"), { duration: 8000 });
              }, 8000);
            } catch (_) {
              setSendKypayToUserWalletPending(false);
              toast.error(t("wallet:kypay.balance_card.bank_to_crypto_Error"));
            }
          }}
          onSetWallet={async () => {
            if (authenticated) await logout();
            dispatch(setAddWallet(true));
          }}
        />
      )}

      {actions.authenticationRequireHelper && (
        <ModalAuthenticationRequireHelper
          open={actions.authenticationRequireHelper as boolean}
          onClose={() => {
            dispatch(setAuthenticationRequireHelper(false));
          }}
        />
      )}

      {isPartnerPage && !identityPartner ? <></> : <FooterTerm />}
    </>
  );
};
